<template>
    <div>
        <!-- Product list -->
        <q-table
            @request="onRequest"
            :filter="table.search"
            :rows="reports"
            :columns="table.cols"
            v-model:pagination="table.pagination"
            :rows-per-page-options="[table.pagination.rowsPerPage]"
            :class="tableDynamicStyle"
            row-key="id"
            binary-state-sort
            :no-data-label="$t('No data available')"
            card-container-class="q-col-gutter-md"
            :grid="$q.screen.lt.md"
        >
            <!-- top bar -->
            <template v-slot:top>
                <div
                    :class="$q.screen.gt.sm ? 'q-py-sm' : ''"
                    class="full-width"
                >
                    <!-- title -->
                    <div class="col-12">
                        <div class="text-h4 text-weight-medium">
                            {{ $t('Automatic Reports') }}
                        </div>
                        <div class="page-description">
                            {{ $t('Receive reports automatically by email') }}
                        </div>
                    </div>
                    <!-- search bar -->
                    <div
                        class="row q-my-md"
                        :class="
                            $q.screen.lt.sm
                                ? ''
                                : 'wrap justify-between items-start content-start'
                        "
                        v-if="!showNoReportMessage"
                    >
                        <q-input
                            v-model="table.search"
                            @clear="table.search = ''"
                            :placeholder="$t('Search reports')"
                            :class="
                                $q.screen.lt.sm
                                    ? 'col-12 q-mb-md'
                                    : 'col q-mr-md'
                            "
                            debounce="750"
                            clearable
                            outlined
                            dense
                            color="accent"
                        >
                            <template v-slot:prepend>
                                <q-icon name="search" />
                            </template>
                        </q-input>
                        <q-btn
                            @click="
                                $router.push({
                                    name: 'automatic_report_create'
                                })
                            "
                            :label="$t('Add Report')"
                            class="table-header__button"
                            :class="$q.screen.lt.sm ? 'col-12' : ''"
                            color="accent"
                            size="md"
                        ></q-btn>
                    </div>
                </div>
            </template>

            <!-- actions column -->
            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <div class="row q-gutter-md">
                        <q-btn
                            id="edit-button"
                            @click="edit(props.row)"
                            class="table__action-button"
                            color="accent"
                            icon="fas fa-edit"
                            outline
                        />
                        <div>
                            <q-btn
                                id="remove-button"
                                @click="handleRemove(props.row.id)"
                                class="table__action-button"
                                icon="fas fa-trash-alt"
                                color="negative"
                                outline
                            />
                        </div>
                    </div>
                </q-td>
            </template>

            <!-- Card template -->
            <template v-slot:item="props">
                <div class="col-12">
                    <automatic-report-card
                        @edit="edit(props.row)"
                        @delete="handleRemove(props.row.id)"
                        :facility="getFacilityLabel(props.row)"
                        :assets="props.row.assets"
                        :type="getReportType(props.row.reportType)"
                        :frequency="
                            getReportFrequency(
                                props.row.frequency,
                                props.row.dayInPeriod
                            )
                        "
                        :time="formatTime(props.row.time)"
                        :dateRangeWindow="props.row.dateRangeWindow"
                        :dateRangeUnit="props.row.dateRangeUnit"
                    >
                    </automatic-report-card>
                </div>
            </template>

            <!-- No reports message -->
            <template v-slot:no-data v-if="showNoReportMessage">
                <div
                    class="full-width column flex-center q-pa-lg"
                    :class="$q.screen.lt.md ? 'border' : ''"
                >
                    <q-icon
                        name="fa-solid fa-calendar-clock"
                        class="q-mt-md icon-bg"
                        color="primary"
                        size="md"
                    />

                    <div class="text-h6 q-mt-md text-center">
                        {{ $t('No report has been scheduled yet.') }}
                    </div>
                    <div class="text-subtitle1 text-secondary">
                        {{
                            $t(
                                'Schedule an automatic report to be sent to your email periodically.'
                            )
                        }}
                    </div>

                    <q-btn
                        @click="
                            $router.push({
                                name: 'automatic_report_create'
                            })
                        "
                        :label="$t('Add Report')"
                        class="table-header__button q-my-lg"
                        :class="$q.screen.lt.sm ? 'col-12' : ''"
                        color="primary"
                        size="md"
                        icon="fas fa-plus"
                    />
                </div>
            </template>
        </q-table>
    </div>
</template>

<script>
import UpdateURL from '@mixins/UpdateURL.vue';
import {
    FREQUENCY_CHOICES,
    REPORT_TYPE_CHOICES,
    REPORT_TYPE_LEGACY_CHOICES,
    WEEKLY_CHOICES,
    YEARLY_CHOICES
} from '@models/automaticreports.js';
import { date } from 'quasar';
import AutomaticReportCard from './AutomaticReportCard.vue';
import { mapActions, mapState } from 'pinia';
import { useAssetsStore } from '@stores/assets.ts';
import { useFacilitiesStore } from '@stores/facilities.ts';
import { useSharedStore } from '@stores/shared.js';
import { getAutomaticReportsPeriodLabel } from '@utils/automaticReportsUtils.js';

export default {
    name: 'AutomaticReportList',
    mixins: [UpdateURL],
    components: {
        AutomaticReportCard
    },
    data() {
        return {
            baseURL: 'periodic_reports/',
            reports: [],

            table: {
                cols: [
                    {
                        align: 'left',
                        field: 'facility',
                        name: 'facility__name',
                        label: this.$t('Facilities'),
                        format: (val, row) => this.getFacilityLabel(row)
                    },
                    {
                        align: 'left',
                        field: 'assets',
                        name: 'assets',
                        label: this.$t('Assets'),
                        sortable: false,
                        style: 'max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;',
                        format: val => {
                            if (val.length <= 0) return this.$t('All');

                            return val
                                .map(assetId => this.getAsset(assetId)?.name)
                                .join(', ');
                        }
                    },
                    {
                        align: 'left',
                        field: 'reportType',
                        name: 'report_type',
                        label: this.$t('Type'),
                        sortable: true,
                        format: val => this.getReportType(val)
                    },
                    {
                        align: 'left',
                        field: 'frequency',
                        name: 'frequency',
                        label: this.$t('Frequency'),
                        sortable: true,
                        format: (val, row) =>
                            this.getReportFrequency(val, row.dayInPeriod)
                    },
                    {
                        align: 'left',
                        field: 'time',
                        name: 'time',
                        label: this.$t('Generation Time'),
                        sortable: true,
                        format: (val, row) =>
                            `${this.formatTime(val)} (${row.timezone})`
                    },
                    {
                        align: 'left',
                        field: 'dateRange',
                        name: 'date_range',
                        label: this.$t('Report Period'),
                        sortable: true,
                        format: (val, row) => this.getReportPeriod(row)
                    },
                    {
                        align: 'left',
                        field: 'actions',
                        name: 'actions',
                        label: this.$t('Actions')
                    }
                ],
                pagination: {
                    sortBy: null,
                    descending: false,
                    page: 1,
                    rowsPerPage: 10,
                    rowsNumber: 0
                },
                search: '',
                isLoading: false
            }
        };
    },
    async created() {
        // update page breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.$t('Reports'),
                route: {
                    name: 'reports_select'
                }
            },
            {
                label: this.$t('Automatic Reports')
            }
        ]);

        this.onRequest({
            pagination: this.table.pagination,
            filter: ''
        });
        await this.loadFacilityGroups();
    },
    computed: {
        ...mapState(useFacilitiesStore, ['getFacility']),
        ...mapState(useAssetsStore, ['getAsset']),
        ...mapState(useFacilitiesStore, ['getFacilityGroup']),
        url() {
            // get current pagination state attributes
            const { descending, page, sortBy } = this.table.pagination;

            let params = {
                search: this.table.search,
                page: page
            };

            if (sortBy) {
                params.ordering = descending ? `-${sortBy}` : `${sortBy}`;
            }

            // update URL
            return this.updateURL({
                baseURL: this.baseURL,
                params: params
            });
        },
        /**
         * Get table style based on screen size and if there are reports.
         * @return {String} - Table class.
         */
        tableDynamicStyle() {
            if (this.$q.screen.gt.sm) {
                if (this.reports.length === 0) {
                    return 'sticky-table';
                }
                return 'sticky-table reports-table';
            }
            return 'reports-table__cardmode';
        },
        /**
         * Show no reports message if there is no report, not loading and no search query.
         * @return {Boolean}
         */
        showNoReportMessage() {
            return (
                this.reports.length === 0 &&
                !this.isLoading &&
                this.table.search === ''
            );
        }
    },
    methods: {
        getAutomaticReportsPeriodLabel,
        ...mapActions(useSharedStore, [
            'beginLoading',
            'stopLoading',
            'setBreadcrumbs'
        ]),
        ...mapActions(useAssetsStore, ['loadAssetsAction']),
        ...mapActions(useFacilitiesStore, ['loadFacilityGroupsAction']),
        /*
         * Request handler for getting automatic reports.
         */
        async onRequest(props) {
            // update pagination
            this.table.pagination = props.pagination;
            this.table.reports = [];

            // begin loading
            this.beginLoading(`${this.$t('Loading automatic reports')}...`);

            // Get automatic reports
            await this.loadAutomaticReports();

            // load assets for each report facility. This is used to display the asset names in the list.
            const allFacilities = this.reports.map(report => report.facility);
            allFacilities.forEach(async facilityId => {
                if (facilityId !== null) {
                    await this.loadAssetsAction({
                        facilityId: facilityId
                    });
                }
            });
        },
        /**
         * Get facility label
         * @param {Object} row - Report object
         * @return {String} - Facility label
         */
        getFacilityLabel(row) {
            if (row.reportType === 'benchmark') {
                if (row.facilities?.length > 0) {
                    return row.facilities
                        .map(facilityId => this.getFacilityName(facilityId))
                        .join(', ');
                } else if (row.facilityGroup) {
                    return this.getFacilityGroupName(row.facilityGroup);
                }
                return '-';
            }

            return this.getFacilityName(row.facility);
        },
        /**
         * Load automatic reports.
         */
        async loadAutomaticReports() {
            this.isLoading = true;
            try {
                const response = await this.$axios.get(this.url);
                this.reports = response.data.results
                    .map(report => {
                        const reportTypeExists =
                            REPORT_TYPE_CHOICES[report.report_type] ||
                            REPORT_TYPE_LEGACY_CHOICES[report.report_type];

                        if (!reportTypeExists) {
                            return null;
                        }

                        return {
                            id: report['id'],
                            facility: report['facility'],
                            facilityGroup: report['facility_group'],
                            facilities: report['facilities'],
                            assets: report['assets'],
                            options: report['options'],
                            frequency: report['frequency'],
                            enabled: report['enabled'],
                            dayInPeriod: report['day_in_period'],
                            timezone: report['timezone'],
                            reportType: report['report_type'],
                            reportFormat: report['report_format'],
                            dateRangeWindow: report['date_range_window'],
                            dateRangeUnit: report['date_range_unit'],
                            time: report['time'],
                            userId: report['user'],
                            resolution: report['resolution'],
                            comparison: report['comparison']
                        };
                    })
                    .filter(report => report !== null);
                this.table.pagination.rowsNumber = response.data.count;
            } catch (error) {
                console.error(
                    'Failed to load automatic reports due to: ',
                    error
                );

                // Show error notification to user
                this.$notifyError(
                    this.$t(
                        'Failed to load automatic reports. Please try again later.'
                    )
                );
            } finally {
                // stop loading
                this.stopLoading();
                this.isLoading = false;
            }
        },
        /**
         * Get facility name
         * @param {Number} - Facility id
         * @return {String} - Facility name
         */
        getFacilityName(facilityId) {
            const facility = this.getFacility(facilityId);
            return facility?.name || '-';
        },
        /**
         * Get facility group name
         * @param {Number} - Facility group id
         * @return {String} - Facility group name
         */
        getFacilityGroupName(facilityGroupId) {
            const facilityGroup = this.getFacilityGroup(facilityGroupId);
            return facilityGroup?.name || '-';
        },
        /**
         * Get report type
         * @param {String} - Type
         * @return {String} - Formatted type
         */
        getReportType(type) {
            const choices = {
                ...REPORT_TYPE_CHOICES,
                ...REPORT_TYPE_LEGACY_CHOICES
            };

            // return default value if type is not found
            // this is to handle new report types for legacy versions that do not have the new report type yet
            if (!choices[type]) {
                return '-';
            }

            return this.$t(choices[type]);
        },
        /**
         * Get report frequency
         * @param {String} - Frequency
         * @return {String} - Formatted frequency
         */
        getReportFrequency(frequency, dayInPeriod) {
            if (frequency === 'weekly') {
                return `${this.$t(FREQUENCY_CHOICES[frequency])} - ${this.$t(
                    WEEKLY_CHOICES[dayInPeriod]
                )}`;
            }
            if (frequency === 'monthly') {
                const newDate = date.buildDate({
                    day: dayInPeriod
                });
                const day = date.formatDate(newDate, 'Do');
                return `${this.$t(
                    FREQUENCY_CHOICES[frequency]
                )} - ${day} ${this.$t('of the month')}`;
            }
            if (frequency === 'yearly') {
                return `${this.$t(FREQUENCY_CHOICES[frequency])} - ${this.$t(
                    YEARLY_CHOICES[dayInPeriod]
                )}`;
            }
            return this.$t(FREQUENCY_CHOICES[frequency]);
        },
        /**
         * Get report period
         * @param {String} - Period
         * @return {String} - Formatted period
         */
        getReportPeriod(row) {
            if (row.dateRangeWindow && row.dateRangeUnit) {
                return getAutomaticReportsPeriodLabel(
                    row.dateRangeWindow,
                    row.dateRangeUnit
                );
            }
            return '-';
        },
        /**
         * Format time
         * @param {String} - Time in hh:mm:ss
         * @return {String} - Formatted time
         */
        formatTime(time) {
            const [hour, minute, second] = time.split(':');
            const newDate = date.buildDate({
                hours: hour,
                minutes: minute,
                seconds: second
            });
            return date.formatDate(newDate, 'HH:mm:ss');
        },
        /**
         * Edit automatic report.
         * @param {Object} row - Automatic report from table row.
         */
        edit(row) {
            this.$router.push({
                name: 'automatic_report_update',
                params: {
                    id: row.id
                },
                state: {
                    facilityLabel: `${this.getFacilityLabel(row)} - ${this.getReportType(row.reportType)}`,
                    facility: row.facility,
                    facilityGroup: row.facilityGroup,
                    facilities: row.facilities.map(facilityId => facilityId),
                    // convert assets to string
                    assets: row.assets.map(asset => asset),
                    // convert options to js object
                    options: JSON.parse(JSON.stringify(row.options)),
                    frequency: row.frequency,
                    dayInPeriod: row.dayInPeriod,
                    timezone: row.timezone,
                    reportType: row.reportType,
                    reportFormat: row.reportFormat,
                    dateRangeWindow: row.dateRangeWindow,
                    dateRangeUnit: row.dateRangeUnit,
                    time: row.time,
                    resolution: row.resolution,
                    comparison: row.comparison
                }
            });
        },
        /**
         * Delete report.
         * @param {Object} row - Report from table row.
         */
        async remove(reportId) {
            // begin loading
            this.beginLoading(`${this.$t('Removing automatic report')}...`);

            try {
                await this.$axios.delete(`${this.baseURL}${reportId}/`);
                this.$notifySuccess(this.$t('Automatic report deleted'));
                await this.loadAutomaticReports();
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to remove automatic report. Please try again later.'
                    ),
                    error
                );
            } finally {
                // stop loading
                this.stopLoading();
            }
        },
        /**
         * Handle remove report.
         * @param {Number} reportId - Report id.
         */
        async handleRemove(reportId) {
            this.$confirmDelete(
                this.$t(
                    'Are you sure you want to delete this automatic report?'
                )
            ).onOk(async () => {
                await this.remove(reportId);
            });
        },
        /**
         * Load facility groups.
         * @return {Promise} A promise that resolves with the response or rejects with an error.
         */
        async loadFacilityGroups() {
            try {
                await this.loadFacilityGroupsAction();
            } catch (error) {
                console.error('Failed to load facility groups due to: ', error);
                this.$notifyError(
                    this.$t(
                        'Failed to load facility groups. Please try again later.'
                    )
                );
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.reports-table {
    height: 80vh;
}

// align table top with cards
.reports-table__cardmode :deep(.q-table__top) {
    padding: 0px;
}

.border {
    border: 1px solid $grey-4;
    border-radius: 5px;
    margin-bottom: 10px;
}

.icon-bg {
    background-color: $secondary-white;
    border-radius: 50%;
    padding: 25px;
}
</style>
